import React from 'react'
import { Button } from './button'
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline'
import { Pagination } from '@thinknimble/tn-models'
import { useTranslation } from 'react-i18next'

interface PaginationProps {
  pagination: Pagination
  isLoading?: boolean
  onNextPage: () => void
  onPrevPage: () => void
  onGoToPage?: (page: number) => void
  showPageNumbers?: boolean
  showTotalCount?: boolean
  className?: string
}

export const PaginationComponent: React.FC<PaginationProps> = ({
  pagination,
  isLoading = false,
  onNextPage,
  onPrevPage,
  onGoToPage,
  showPageNumbers = true,
  showTotalCount = true,
  className = '',
}) => {
  const { t } = useTranslation()
  const { page: currentPage, hasNextPage, hasPrevPage, totalCount } = pagination
  const totalPages = pagination.calcTotalPages(pagination)
  const handleNextPage = () => {
    if (hasNextPage && !isLoading) {
      onNextPage()
    }
  }

  const handlePrevPage = () => {
    if (hasPrevPage && !isLoading) {
      onPrevPage()
    }
  }

  const handleGoToPage = (page: number) => {
    if (onGoToPage && page !== currentPage && page >= 1 && page <= totalPages) {
      onGoToPage(page)
    }
  }

  // Generate page numbers to show (with ellipsis for large page counts)
  const getPageNumbers = () => {
    const pages: (number | string)[] = []
    const maxVisiblePages = 5

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i)
      }
    } else {
      // Show first page
      pages.push(1)

      if (currentPage > 3) {
        pages.push('...')
      }

      // Show pages around current page
      const start = Math.max(2, currentPage - 1)
      const end = Math.min(totalPages - 1, currentPage + 1)

      for (let i = start; i <= end; i++) {
        pages.push(i)
      }

      if (currentPage < totalPages - 2) {
        pages.push('...')
      }

      // Show last page
      if (totalPages > 1) {
        pages.push(totalPages)
      }
    }

    return pages
  }

  return (
    <div className={`flex items-center justify-between gap-4 ${className}`}>
      {/* Total count info */}
      {showTotalCount && totalCount !== undefined && (
        <div className="text-sm text-text-secondary">
          {t('common.showing', { count: totalCount })}
        </div>
      )}

      {/* Pagination controls */}
      <div className="flex items-center gap-2">
        {/* Previous button */}
        <Button
          disabled={!hasPrevPage || isLoading}
          onClick={handlePrevPage}
          className="flex items-center gap-1"
        >
          <ChevronLeftIcon className="h-3 w-3" />
          {t('common.previous')}
        </Button>

        {/* Page numbers */}
        {showPageNumbers && onGoToPage && (
          <div className="flex items-center gap-1">
            {getPageNumbers().map((page, index) => (
              <React.Fragment key={index}>
                {page === '...' ? (
                  <span className="px-2 py-1 text-text-tertiary">...</span>
                ) : (
                  <Button
                    variant={currentPage === page ? 'primary' : 'ghost'}
                    size="sm"
                    disabled={isLoading}
                    onClick={() => handleGoToPage(page as number)}
                    className="min-w-[40px] text-sm"
                  >
                    {page}
                  </Button>
                )}
              </React.Fragment>
            ))}
          </div>
        )}

        {/* Next button */}
        <Button
          disabled={!hasNextPage || isLoading}
          onClick={handleNextPage}
          className="flex items-center gap-1"
        >
          {t('common.next')}
          <ChevronRightIcon className="h-3 w-3" />
        </Button>
      </div>

      {/* Page info */}
      <div className="text-sm text-text-secondary">
        {t('common.page')} {currentPage} {t('common.of')} {totalPages}
      </div>
    </div>
  )
}
